import { useEffect, useReducer } from "react";
import { Users, Truck, Scale } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { AsYouType as PhoneNumberFormatterAsYouType } from "libphonenumber-js";
import {
  parsePhoneNumberToE164Strict,
  zodPhoneNumberRefine,
} from "@/lib/validation/phoneNumber";
import {
  useOnboardAuthUser,
  OnboardAuthUserPermissionInput,
  NON_CLIENT_PERMISSION_WHITELIST,
  isPermissionAllowedForNonClient,
} from "@/lib/api/betterAuth";
import { CustomerMultiPicker } from "./CustomerMultiPicker";

// --- Permission catalog (mirror of PermissionsEditor's PERMISSION_TYPES) ---
// Mirrors libs/stytch-auth/src/lib/betterAuthPermissions.ts. Update both together
// when adding new permission types.

type PermissionKey =
  | "mobile"
  | "risk_management"
  | "market_analysis"
  | "freight_ticket_upload"
  | "weight_ticket";

interface PermissionCatalogEntry {
  key: PermissionKey;
  label: string;
  description: string;
  // The exact sub_types we send to the onboard endpoint when this permission is
  // enabled. Matches PermissionsEditor's hard-coded values for consistency.
  subTypes: string[];
  relationType: "risk" | "freight" | "weight_ticket" | null;
  // max customers selectable when this permission has a relation
  maxCustomers?: number;
  // Force-on (e.g. mobile). UI shows it as disabled and always-on.
  forcedOn?: boolean;
  // Header title + icon for the relation picker collapsible (step 3).
  pickerTitle?: string;
  pickerIcon?: React.ReactNode;
  defaultOn?: boolean;
}

const PERMISSION_CATALOG: PermissionCatalogEntry[] = [
  {
    key: "mobile",
    label: "Mobile (login)",
    description: "Required. Mobile login and self-access. Cannot be disabled.",
    subTypes: ["login", "self"],
    relationType: null,
    forcedOn: true,
  },
  {
    key: "risk_management",
    label: "Risk Management",
    description: "Access to risk management features and data",
    subTypes: ["read", "self"],
    relationType: "risk",
    pickerTitle: "Risk Profiles",
    pickerIcon: <Users className="h-4 w-4" />,
  },
  {
    key: "market_analysis",
    label: "Market Analysis",
    description: "Access to market analysis tools and reports",
    subTypes: ["read", "self"],
    relationType: null,
    defaultOn: true
  },
  {
    key: "freight_ticket_upload",
    label: "Freight Ticket Upload",
    description: "Access to upload and manage freight tickets",
    subTypes: ["read", "self"],
    relationType: "freight",
    maxCustomers: 1,
    pickerTitle: "Freight Customers",
    pickerIcon: <Truck className="h-4 w-4" />,
  },
  {
    key: "weight_ticket",
    label: "Tickets & Settlements",
    description: "Access to view a user's Weight Tickets and Settlements",
    subTypes: ["read", "self"],
    relationType: "weight_ticket",
    maxCustomers: 1,
    pickerTitle: "Weight Ticket Customers",
    pickerIcon: <Scale className="h-4 w-4" />,
  },
];

// --- Identity step zod schema ---

const IdentitySchema = z.object({
  name: z.string().min(1, "Name is required"),
  phoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .transform(parsePhoneNumberToE164Strict)
    .refine((value: string | undefined): value is string => value !== undefined, {
      message: "Please enter a valid phone number",
    })
    .refine(zodPhoneNumberRefine("phoneNumber"), {
      message: "Please enter a valid phone number",
    }),
  email: z
    .union([z.literal(""), z.string().email("Please enter a valid email")])
    .optional(),
});

// --- Reducer ---

type Step = 1 | 2 | 3;

interface PermissionState {
  enabled: boolean;
  customerIds: string[];
  customerSearch: string;
  // Step 3 collapsible state — all start collapsed.
  pickerOpen: boolean;
}

interface OnboardState {
  step: Step;
  isSubmitting: boolean;
  // Step 1
  name: string;
  phoneInput: string; // formatted-as-you-type display value
  email: string;
  // Step 1 derived (after Next)
  phoneE164: string | null;
  identityErrors: { name?: string; phoneNumber?: string; email?: string };
  // Step 2 / 3
  permissions: Record<PermissionKey, PermissionState>;
}

type OnboardAction =
  | { type: "SET_NAME"; value: string }
  | { type: "SET_PHONE_INPUT"; value: string }
  | { type: "SET_EMAIL"; value: string }
  | {
      type: "SUBMIT_IDENTITY";
      ok: true;
      phoneE164: string;
    }
  | {
      type: "SUBMIT_IDENTITY";
      ok: false;
      errors: { name?: string; phoneNumber?: string; email?: string };
    }
  | { type: "TOGGLE_PERMISSION"; key: PermissionKey; enabled: boolean }
  | { type: "SET_CUSTOMERS"; key: PermissionKey; customerIds: string[] }
  | { type: "SET_CUSTOMER_SEARCH"; key: PermissionKey; value: string }
  | { type: "SET_PICKER_OPEN"; key: PermissionKey; open: boolean }
  | { type: "GOTO_STEP"; step: Step }
  | { type: "BEGIN_SUBMIT" }
  | { type: "END_SUBMIT" }
  | { type: "RESET" };

function initialPermissions(): Record<PermissionKey, PermissionState> {
  const result = {} as Record<PermissionKey, PermissionState>;
  for (const entry of PERMISSION_CATALOG) {
    result[entry.key] = {
      enabled: entry.defaultOn || entry.forcedOn === true,
      customerIds: [],
      customerSearch: "",
      pickerOpen: false,
    };
  }
  return result;
}

function initialState(): OnboardState {
  return {
    step: 1,
    isSubmitting: false,
    name: "",
    phoneInput: "",
    email: "",
    phoneE164: null,
    identityErrors: {},
    permissions: initialPermissions(),
  };
}

function reducer(state: OnboardState, action: OnboardAction): OnboardState {
  switch (action.type) {
    case "SET_NAME":
      return { ...state, name: action.value, identityErrors: { ...state.identityErrors, name: undefined } };
    case "SET_PHONE_INPUT":
      return {
        ...state,
        phoneInput: action.value,
        identityErrors: { ...state.identityErrors, phoneNumber: undefined },
      };
    case "SET_EMAIL":
      return {
        ...state,
        email: action.value,
        identityErrors: { ...state.identityErrors, email: undefined },
      };
    case "SUBMIT_IDENTITY":
      if (action.ok) {
        return { ...state, phoneE164: action.phoneE164, identityErrors: {}, step: 2 };
      }
      return { ...state, identityErrors: action.errors };
    case "TOGGLE_PERMISSION": {
      const catalog = PERMISSION_CATALOG.find((p: PermissionCatalogEntry) => p.key === action.key);
      if (catalog?.forcedOn) return state; // cannot toggle mobile
      const prev = state.permissions[action.key];
      const nextEnabled = action.enabled;
      return {
        ...state,
        permissions: {
          ...state.permissions,
          [action.key]: {
            ...prev,
            enabled: nextEnabled,
            // wipe customers when turning off
            customerIds: nextEnabled ? prev.customerIds : [],
          },
        },
      };
    }
    case "SET_CUSTOMERS":
      return {
        ...state,
        permissions: {
          ...state.permissions,
          [action.key]: {
            ...state.permissions[action.key],
            customerIds: action.customerIds,
          },
        },
      };
    case "SET_CUSTOMER_SEARCH":
      return {
        ...state,
        permissions: {
          ...state.permissions,
          [action.key]: {
            ...state.permissions[action.key],
            customerSearch: action.value,
          },
        },
      };
    case "SET_PICKER_OPEN":
      return {
        ...state,
        permissions: {
          ...state.permissions,
          [action.key]: {
            ...state.permissions[action.key],
            pickerOpen: action.open,
          },
        },
      };
    case "GOTO_STEP":
      return { ...state, step: action.step };
    case "BEGIN_SUBMIT":
      return { ...state, isSubmitting: true };
    case "END_SUBMIT":
      return { ...state, isSubmitting: false };
    case "RESET":
      return initialState();
    default:
      return state;
  }
}

// --- Component ---

export interface OnboardUserModalProps {
  open: boolean;
  onClose: () => void;
  organizationId?: string;
  isNonClient: boolean;
}

export function OnboardUserModal({
  open,
  onClose,
  organizationId,
  isNonClient,
}: OnboardUserModalProps) {
  const { toast } = useToast();
  const onboardMutation = useOnboardAuthUser();
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  useEffect(() => {
    if (!open) {
      dispatch({ type: "RESET" });
    }
  }, [open]);

  // Filter the catalog to what's selectable in this tab's context. Mobile is
  // always shown (forced-on). Non-client view drops anything not in the whitelist.
  const visiblePermissions = PERMISSION_CATALOG.filter((p: PermissionCatalogEntry) => {
    if (p.forcedOn) return true;
    if (!isNonClient) return true;
    return isPermissionAllowedForNonClient(p.key);
  });

  // Relation-bearing permissions that were enabled in Step 2 — drives Step 3.
  const relationPermissionsToShow = visiblePermissions.filter(
    (p: PermissionCatalogEntry) =>
      p.relationType !== null && state.permissions[p.key].enabled
  );

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatter = new PhoneNumberFormatterAsYouType("US");
    const formatted = formatter.input(e.target.value) ?? "";
    dispatch({ type: "SET_PHONE_INPUT", value: formatted });
  };

  const handleNextFromStep1 = () => {
    const parseResult = IdentitySchema.safeParse({
      name: state.name.trim(),
      phoneNumber: state.phoneInput,
      email: state.email.trim() || undefined,
    });
    if (!parseResult.success) {
      const errs: { name?: string; phoneNumber?: string; email?: string } = {};
      for (const issue of parseResult.error.errors) {
        const field = issue.path[0];
        if (field === "name" && !errs.name) errs.name = issue.message;
        else if (field === "phoneNumber" && !errs.phoneNumber) errs.phoneNumber = issue.message;
        else if (field === "email" && !errs.email) errs.email = issue.message;
      }
      dispatch({ type: "SUBMIT_IDENTITY", ok: false, errors: errs });
      return;
    }
    dispatch({
      type: "SUBMIT_IDENTITY",
      ok: true,
      phoneE164: parseResult.data.phoneNumber,
    });
  };

  const handleNextFromStep2 = () => {
    if (relationPermissionsToShow.length === 0) {
      void handleSubmit();
      return;
    }
    dispatch({ type: "GOTO_STEP", step: 3 });
  };

  const validateStep3 = (): string | null => {
    for (const p of relationPermissionsToShow) {
      const sel = state.permissions[p.key].customerIds;
      if (sel.length === 0) {
        return `Select at least one customer for ${p.label}`;
      }
      if (p.maxCustomers !== undefined && sel.length > p.maxCustomers) {
        return `${p.label} allows at most ${p.maxCustomers} customer${p.maxCustomers === 1 ? "" : "s"}`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!organizationId) {
      toast({ title: "Error", description: "Organization ID is required", variant: "destructive" });
      return;
    }
    if (!state.phoneE164) {
      toast({ title: "Error", description: "Phone number missing", variant: "destructive" });
      return;
    }
    if (relationPermissionsToShow.length > 0) {
      const stepErr = validateStep3();
      if (stepErr) {
        toast({ title: "Validation Error", description: stepErr, variant: "destructive" });
        return;
      }
    }

    const permissionsPayload: OnboardAuthUserPermissionInput[] = visiblePermissions
      .filter((p: PermissionCatalogEntry) => state.permissions[p.key].enabled)
      .map((p: PermissionCatalogEntry) => ({
        permissionType: p.key,
        subTypes: p.subTypes,
        ...(p.relationType
          ? { customerIds: state.permissions[p.key].customerIds }
          : {}),
      }));

    dispatch({ type: "BEGIN_SUBMIT" });
    try {
      const result = await onboardMutation.mutateAsync({
        organizationId,
        phoneNumber: state.phoneE164,
        name: state.name.trim(),
        email: state.email.trim() || undefined,
        isNonClient,
        permissions: permissionsPayload,
      });
      if (!result.success) {
        throw result;
      }
      toast({ title: "Success", description: "User onboarded successfully" });
      dispatch({ type: "RESET" });
      onClose();
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message: unknown }).message)
          : "Failed to onboard user";
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      dispatch({ type: "END_SUBMIT" });
    }
  };

  const handleCloseRequest = () => {
    if (state.isSubmitting) return;
    dispatch({ type: "RESET" });
    onClose();
  };

  const stepLabel = `Step ${state.step} of 3`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleCloseRequest(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Onboard {isNonClient ? "Non-Client" : "Client"} User
          </DialogTitle>
          <p className="text-sm text-gray-500">{stepLabel}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {state.step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="onboard-name">Name</Label>
                <Input
                  id="onboard-name"
                  value={state.name}
                  onChange={(e) => dispatch({ type: "SET_NAME", value: e.target.value })}
                  placeholder="Enter user name"
                  disabled={state.isSubmitting}
                />
                {state.identityErrors.name && (
                  <p className="text-red-500 text-sm">{state.identityErrors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="onboard-phone">Phone Number</Label>
                <Input
                  id="onboard-phone"
                  type="tel"
                  value={state.phoneInput}
                  onChange={handlePhoneInputChange}
                  placeholder="Enter phone number"
                  disabled={state.isSubmitting}
                />
                {state.identityErrors.phoneNumber && (
                  <p className="text-red-500 text-sm">{state.identityErrors.phoneNumber}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="onboard-email">Email (optional)</Label>
                <Input
                  id="onboard-email"
                  type="email"
                  value={state.email}
                  onChange={(e) => dispatch({ type: "SET_EMAIL", value: e.target.value })}
                  placeholder="user@example.com"
                  disabled={state.isSubmitting}
                />
                {state.identityErrors.email && (
                  <p className="text-red-500 text-sm">{state.identityErrors.email}</p>
                )}
                <p className="text-xs text-gray-500">
                  When blank, a welcome email is not sent.
                </p>
              </div>
            </div>
          )}

          {state.step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Select permissions to grant. Mobile is always included.
                {isNonClient && (
                  <span className="block mt-1 text-xs text-gray-500">
                    Non-client users are restricted to:{" "}
                    {NON_CLIENT_PERMISSION_WHITELIST.join(", ")}.
                  </span>
                )}
              </p>

              <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-700">
                <div className="col-span-7">Permission</div>
                <div className="col-span-3">Access Level</div>
                <div className="col-span-2 text-right">Enabled</div>
              </div>

              <div className="space-y-1">
                {visiblePermissions.map((entry: PermissionCatalogEntry) => {
                  const ps = state.permissions[entry.key];
                  return (
                    <div
                      key={entry.key}
                      className="grid grid-cols-12 gap-4 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="col-span-7 flex flex-col">
                        <span className="font-medium text-gray-900">{entry.label}</span>
                        <span className="text-xs text-gray-500 mt-0.5">{entry.description}</span>
                      </div>
                      <div className="col-span-3 flex items-center">
                        {ps.enabled ? (
                          <div className="flex items-center gap-1">
                            <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm text-gray-600">{entry.subTypes.join(", ")}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <div className="h-2 w-2 bg-gray-300 rounded-full"></div>
                            <span className="text-sm text-gray-400">No access</span>
                          </div>
                        )}
                      </div>
                      <div className="col-span-2 flex justify-end items-center">
                        <Switch
                          checked={ps.enabled}
                          onCheckedChange={(enabled) =>
                            dispatch({ type: "TOGGLE_PERMISSION", key: entry.key, enabled })
                          }
                          disabled={entry.forcedOn || state.isSubmitting}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {state.step === 3 && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Assign customers for each relation-bearing permission.
              </p>
              {relationPermissionsToShow.map((entry: PermissionCatalogEntry) => {
                const ps = state.permissions[entry.key];
                const description =
                  entry.maxCustomers !== undefined
                    ? `Select up to ${entry.maxCustomers} customer${entry.maxCustomers === 1 ? "" : "s"}.`
                    : "Select one or more customers.";
                return (
                  <CustomerMultiPicker
                    key={entry.key}
                    relationType={entry.relationType as "risk" | "freight" | "weight_ticket"}
                    title={entry.pickerTitle ?? entry.label}
                    icon={entry.pickerIcon}
                    description={description}
                    selectedCustomerIds={ps.customerIds}
                    onChange={(next: string[]) =>
                      dispatch({ type: "SET_CUSTOMERS", key: entry.key, customerIds: next })
                    }
                    searchQuery={ps.customerSearch}
                    onSearchQueryChange={(value: string) =>
                      dispatch({ type: "SET_CUSTOMER_SEARCH", key: entry.key, value })
                    }
                    isOpen={ps.pickerOpen}
                    onOpenChange={(open: boolean) =>
                      dispatch({ type: "SET_PICKER_OPEN", key: entry.key, open })
                    }
                    maxAllowed={entry.maxCustomers}
                    disabled={state.isSubmitting}
                  />
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleCloseRequest}
            disabled={state.isSubmitting}
          >
            Cancel
          </Button>

          {state.step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                dispatch({
                  type: "GOTO_STEP",
                  step: (state.step - 1) as Step,
                })
              }
              disabled={state.isSubmitting}
            >
              Back
            </Button>
          )}

          {state.step === 1 && (
            <Button type="button" onClick={handleNextFromStep1} disabled={state.isSubmitting}>
              Next
            </Button>
          )}

          {state.step === 2 && (
            <Button type="button" onClick={handleNextFromStep2} disabled={state.isSubmitting}>
              {relationPermissionsToShow.length === 0 ? "Onboard User" : "Next"}
            </Button>
          )}

          {state.step === 3 && (
            <Button type="button" onClick={handleSubmit} disabled={state.isSubmitting}>
              {state.isSubmitting ? "Onboarding..." : "Onboard User"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
